import { Avatar, type MantineColor } from '@mantine/core';
import { initials } from '@/lib/format';

export type PersonAvatarSize = 'sm' | 'md' | 'lg' | 'xl';

const SIZE_PX: Record<PersonAvatarSize, number> = { sm: 36, md: 44, lg: 72, xl: 112 };

interface Props {
  name?: string | null;
  /** Photo URL; blank / null shows the initials. A URL that fails to load also falls back to initials. */
  src?: string | null;
  size?: PersonAvatarSize | number;
  color?: MantineColor;
  /** Defaults to "Photo of {name}". */
  alt?: string;
  /** The name is printed right beside it — hide it from screen readers so it isn't read twice. */
  decorative?: boolean;
  className?: string;
}

/** Round profile photo with an initials fallback (lazy-loaded, no referrer sent to the image host). */
export function PersonAvatar({ name, src, size = 'md', color = 'brand', alt, decorative, className }: Props) {
  const px = typeof size === 'number' ? size : SIZE_PX[size];
  const url = src?.trim() ? src.trim() : null;
  const label = alt ?? (name ? `Photo of ${name}` : 'Profile photo');
  return (
    <Avatar
      src={url}
      alt={url ? (decorative ? '' : label) : undefined}
      aria-hidden={decorative ? true : undefined}
      role={!decorative && !url ? 'img' : undefined}
      aria-label={!decorative && !url ? (name ?? 'Profile') : undefined}
      size={px}
      radius="100%"
      color={color}
      variant="light"
      className={className}
      imageProps={{ loading: 'lazy', decoding: 'async', referrerPolicy: 'no-referrer', draggable: false }}
      style={{ flex: '0 0 auto' }}
    >
      {initials(name)}
    </Avatar>
  );
}
